import { action, IReactionDisposer, observable, when } from 'mobx';

export const MAX_CONCURRENT_PANEL_LOADS = 8;

export type PanelLoadPriority = [distance: number, top: number, left: number];

export interface PanelLoadEntry {
  run: () => void;
  cancel: () => void;
  isRunning: () => boolean;
  getPriority: () => PanelLoadPriority;
}

interface BoardLoadSchedulerOptions {
  shouldLoadAll?: () => boolean;
  maxConcurrent?: number;
}

function comparePriority(a: PanelLoadPriority, b: PanelLoadPriority) {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

export default class BoardLoadScheduler {
  private registered = new Map<string, PanelLoadEntry>();

  private running = new Map<string, IReactionDisposer>();

  private pending = observable.set<string>();

  private inView = observable.set<string>();

  private readonly shouldLoadAll: () => boolean;

  private readonly maxConcurrent: number;

  private dispatchTimer?: ReturnType<typeof setTimeout>;

  constructor({
    shouldLoadAll = () => false,
    maxConcurrent = MAX_CONCURRENT_PANEL_LOADS
  }: BoardLoadSchedulerOptions = {}) {
    this.shouldLoadAll = shouldLoadAll;
    this.maxConcurrent = maxConcurrent;
  }

  isPending(id: string) {
    return this.pending.has(id);
  }

  isRegistered(id: string) {
    return this.registered.has(id);
  }

  @action
  register(id: string, entry: PanelLoadEntry) {
    this.registered.set(id, entry);
    this.requestDispatch();
    return () => this.unregister(id);
  }

  @action
  private unregister(id: string) {
    this.registered.delete(id);
    this.pending.delete(id);
    this.inView.delete(id);
    this.running.get(id)?.();
    this.running.delete(id);
    this.requestDispatch();
  }

  @action
  setInView({ id, isInView }: { id: string; isInView: boolean }) {
    if (isInView) {
      this.inView.add(id);
      this.requestDispatch();
    } else {
      this.inView.delete(id);
    }
  }

  // may arrive before register() (child effects run first); waits for registration
  @action
  schedule(id: string) {
    const entry = this.registered.get(id);
    if (entry && this.running.has(id) && this.shouldLoad(id)) {
      // already holds a slot; re-run in place instead of re-queuing
      this.pending.delete(id);
      entry.run();
    } else {
      // the queued re-run supersedes the current subscription
      entry?.cancel();
      this.pending.add(id);
      this.requestDispatch();
    }
  }

  @action
  reset() {
    clearTimeout(this.dispatchTimer);
    this.dispatchTimer = undefined;
    this.running.forEach((dispose) => dispose());
    this.running.clear();
    this.pending.clear();
    this.inView.clear();
  }

  private shouldLoad(id: string) {
    return this.shouldLoadAll() || this.inView.has(id);
  }

  // requests arrive one panel at a time; batching lets each pass rank all eligible panels
  private requestDispatch() {
    if (this.dispatchTimer === undefined) {
      this.dispatchTimer = setTimeout(() => {
        this.dispatchTimer = undefined;
        this.dispatch();
      });
    }
  }

  @action
  private dispatch() {
    const freeSlots = this.maxConcurrent - this.running.size;
    if (freeSlots <= 0) {
      return;
    }

    const next = [...this.pending]
      .filter((id) => this.registered.has(id) && !this.running.has(id) && this.shouldLoad(id))
      .map((id) => ({ id, priority: (this.registered.get(id) as PanelLoadEntry).getPriority() }))
      .sort((a, b) => comparePriority(a.priority, b.priority))
      .slice(0, freeSlots);

    next.forEach(({ id }) => this.start(id));
  }

  @action
  private start(id: string) {
    this.pending.delete(id);

    const entry = this.registered.get(id) as PanelLoadEntry;
    entry.run();

    let settled = false;
    const dispose = when(
      () => !entry.isRunning(),
      () => {
        settled = true;
        this.running.delete(id);
        this.dispatch();
      }
    );

    // `when` can settle synchronously outside an action batch
    if (!settled) {
      this.running.set(id, dispose);
    }
  }
}
